import { makeStyles } from '@fluentui/react-components';

import { IDENTIFIER, STATUS, TEXT } from '../theme/tokens';

const useStyles = makeStyles({
  // Neutral, never red and never a warning colour. "No group" is the ORDINARY
  // state of most migrations — styling it as a problem would push operators
  // into busywork filing campaigns that never needed filing.
  //
  // FD-467 — was `#71717a` / `#a16207`, zinc-500 and yellow-700 from the
  // retired Tailwind palette. Both now come from the token module, so the next
  // accent or status change reaches them.
  none: {
    color: TEXT.secondary,
  },
  dangling: {
    // A real data problem, but not a failure — the operator-attention rung,
    // which is the darker label value rather than the graphic amber.
    color: STATUS.warningText,
  },
  // FD-467 — this read `var(--font-mono, ui-monospace, monospace)`, and
  // `--font-mono` is defined NOWHERE in the app. Every render therefore took
  // the fallback and drew a dangling group id in the system monospace family,
  // on a label that appears on every campaign row. Invisible to the grep that
  // found the `font-mono` CLASS sites, and found instead by reading the built
  // bundle for `ui-monospace`.
  identifier: {
    ...IDENTIFIER,
    fontSize: '12px',
  },
});

export interface GroupLabelProps {
  migrationGroupId: string | null | undefined;
  migrationGroupName: string | null | undefined;
}

// Three states, deliberately distinguishable:
//
//   no group            → the words "No group", NOT an em-dash. "—" reads as
//                         DATA MISSING; "No group" reads as A STATE.
//   grouped             → the group's name.
//   id without a name   → a DANGLING reference — the group was deleted, or the
//                         deployment's customerEnvironmentId changed
//                         (FD-ADR-030 §7). Shown as the raw id so the evidence
//                         survives; collapsing it to "No group" would hide a
//                         real problem inside an entirely ordinary state.
export default function GroupLabel({ migrationGroupId, migrationGroupName }: GroupLabelProps) {
  const styles = useStyles();

  if (!migrationGroupId) {
    return <span className={styles.none}>No group</span>;
  }
  if (!migrationGroupName) {
    return (
      <span className={styles.dangling} title="This group no longer exists">
        <span className={styles.identifier}>{migrationGroupId}</span> (missing)
      </span>
    );
  }
  return <span>{migrationGroupName}</span>;
}
